
import { useState, useEffect, useCallback } from 'react';

declare global {
  interface Window {
    grecaptcha: {
      ready: (callback: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}

// Chiave del sito reCAPTCHA v3 (safe to expose)
const RECAPTCHA_SITE_KEY = '6LcGhQMrAAAAABa7aunEWaWbPqIpNIfwKfPTZJhS';

export function useRecaptcha() {
  const [isRecaptchaLoaded, setIsRecaptchaLoaded] = useState(false);
  const [recaptchaError, setRecaptchaError] = useState<Error | null>(null);
  
  // Verifica se reCAPTCHA è già caricato
  useEffect(() => {
    // Se grecaptcha è già disponibile globalmente
    if (window.grecaptcha) {
      window.grecaptcha.ready(() => {
        setIsRecaptchaLoaded(true);
      });
      return;
    }
    
    // Verifica se lo script è già presente nel DOM
    const existingScript = document.querySelector('script[src*="recaptcha/api.js"]');
    if (existingScript) {
      // Lo script è già presente, attendiamo che sia caricato
      const checkRecaptchaLoaded = setInterval(() => {
        if (window.grecaptcha) {
          clearInterval(checkRecaptchaLoaded);
          window.grecaptcha.ready(() => {
            setIsRecaptchaLoaded(true);
          });
        }
      }, 100);
      
      // Timeout di sicurezza dopo 5 secondi
      setTimeout(() => {
        clearInterval(checkRecaptchaLoaded);
        if (!window.grecaptcha) {
          setRecaptchaError(new Error('Timeout durante il caricamento di reCAPTCHA'));
        }
      }, 5000);
      
      return;
    }
    
    // Carica lo script di reCAPTCHA se non è già presente
    try {
      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
      script.async = true;
      script.defer = true;
      
      script.onload = () => {
        window.grecaptcha.ready(() => {
          setIsRecaptchaLoaded(true);
        });
      };
      
      script.onerror = () => {
        setRecaptchaError(new Error('Impossibile caricare reCAPTCHA'));
      };
      
      document.head.appendChild(script);
    } catch (error) {
      setRecaptchaError(error instanceof Error ? error : new Error('Errore sconosciuto durante il caricamento di reCAPTCHA'));
    }
  }, []);
  
  // Funzione per eseguire reCAPTCHA - solo genera token, verifica server-side
  const executeRecaptcha = useCallback(async (action: string): Promise<string> => {
    if (!isRecaptchaLoaded) {
      throw new Error('reCAPTCHA non è ancora caricato');
    }
    
    try {
      const token = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action });
      console.log('[useRecaptcha] Token generated successfully for action:', action);
      return token;
    } catch (error) {
      console.error('Errore durante l\'esecuzione di reCAPTCHA:', error);
      throw error instanceof Error ? error : new Error('Errore sconosciuto durante l\'esecuzione di reCAPTCHA');
    }
  }, [isRecaptchaLoaded]);
  
  return { isRecaptchaLoaded, executeRecaptcha, recaptchaError };
}
